import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { Card, Button, ListGroup, Badge, Spinner, Alert } from 'react-bootstrap';
import { XCircle, CheckCircle } from 'react-bootstrap-icons';
import UserSearchBar from '../UserSearchBar/UserSearchBar';
import { useLanguage } from '../../../../API/Context/LanguageContext';
import { VocabButtons as btn_vocab } from '../../../../ConfigFiles/multilingual/VocabButtons';
import type { AuthorizedTranslatedUser } from '../../../../Types/schema-helpers';
import './UserListWidget.css'

export interface UserListWidgetProps {
  title: string;
  subtitle?: string;
  emptyText: string;
  authorizedHeader: string;
  initialUsers: AuthorizedTranslatedUser[];
  initialSearchQuery?: string;
  onSearchUser: (username: string) => Promise<string | null>;
  onSave: (users: AuthorizedTranslatedUser[]) => Promise<void>;
  onSaveSuccess?: () => void;
}

export const UserListWidget: React.FC<UserListWidgetProps> = ({
  title,
  subtitle,
  emptyText,
  authorizedHeader,
  initialUsers,
  initialSearchQuery,
  onSearchUser,
  onSave,
  onSaveSuccess,
}) => {
  const { lan } = useLanguage();
  const [accessList, setAccessList] = useState<AuthorizedTranslatedUser[]>([]);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Sync state with incoming props
  useEffect(() => {
    setAccessList(initialUsers || []);
  }, [initialUsers]);

  // Compute unsaved changes
  const hasChanges = useMemo(() => {
    const originalSorted = (initialUsers || []).map((u) => u.username).sort().join(',');
    const currentSorted = accessList.map((u) => u.username).sort().join(',');
    return originalSorted !== currentSorted;
  }, [initialUsers, accessList]);

  const handleAddUser = useCallback((user: AuthorizedTranslatedUser) => {
    setAccessList((prev) => {
      if (prev.some((u) => u.username === user.username)) return prev;
      return [...prev, user];
    });
  }, []);

  const handleRemoveUser = useCallback((username: string) => {
    setAccessList((prev) => prev.filter((u) => u.username !== username));
  }, []);

  const handleSave = async () => {
    setIsSaving(true);
    setError(null);
    try {
      await onSave(accessList);
      if (onSaveSuccess) onSaveSuccess();
    } catch (err) {
      console.error(err);
      setError('An error occurred while saving.');
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div className="d-flex flex-column gap-3">
      {/* Top Header & Save Action */}
      <div className="d-flex justify-content-between align-items-center pb-2 border-bottom">
        <div>
          <h3 className="m-0 fw-bold">{title}</h3>
          {subtitle && <small className="text-muted">{subtitle}</small>}
        </div>

        <Button
          variant="primary"
          size="sm"
          onClick={handleSave}
          disabled={!hasChanges || isSaving}
          className="d-flex align-items-center gap-2 shadow-sm"
        >
          {isSaving ? <Spinner size="sm" animation="border" /> : <CheckCircle size={16} />}
          <span>{btn_vocab.save_changes[lan] || 'Save'}</span>
        </Button>
      </div>

      {error && <Alert variant="danger">{error}</Alert>}

      <Card className="shadow-sm border-0">
        <Card.Body>
          {/* Reusable Search Bar */}
          <UserSearchBar
            initialValue={initialSearchQuery}
            onSearch={onSearchUser}
            onUserFound={handleAddUser}
          />

          <h6 className="mt-4 fw-bold">{authorizedHeader}</h6>

          {accessList.length > 0 ? (
            <ListGroup variant="flush" className="border rounded">
  {accessList.map((user) => (
    <ListGroup.Item
      key={user.username}
      className="d-flex justify-content-between align-items-center py-2 px-3"
    >
      <div>
        <span className="me-2 text-dark">{user.name}</span>
        <span className="username-highlight fw-bold">@{user.username}</span>
      </div>
      <Button
        variant="link"
        className="text-danger p-0"
        onClick={() => handleRemoveUser(user.username)}
      >
        <XCircle size={18} />
      </Button>
    </ListGroup.Item>
  ))}
</ListGroup>
          ) : (
            <Badge bg="secondary" className="p-2 fw-normal">
              {emptyText}
            </Badge>
          )}
        </Card.Body>
      </Card>
    </div>
  );
};

export default UserListWidget;